import { useSyncExternalStore } from 'react';
import {
  ANGLES_D_OUVERTURE,
  FICHE_PAR_DEFAUT,
  FONDS_DE_PROJET,
  FORMES_DE_PROJET,
  HABIT_PAR_DEFAUT,
  PAPIERS_DE_FICHE,
  TYPES_DE_FICHE,
  type AngleDOuverture,
  type FondDeProjet,
  type FormeDeProjet,
  type PapierDeFiche,
  type TypeDeFiche,
} from '@nextilt/commun';

/**
 * L'apparence choisie (chantier apparence, modifications 01 et 02) : l'ambiance (Carnet papier,
 * Clair, Sombre), l'écriture à la main (oui ou non), le relief et les ombres (Doux, Marqué), le
 * grain du papier (oui ou non), et ce qu'un projet neuf reçoit par défaut : la forme de ses bulles,
 * le type et le papier de ses fiches (chantier types-de-fiche, modification 06), l'angle sur lequel
 * il s'ouvre (chantier angle-a-l-ouverture). Des réglages
 * indépendants.
 *
 * La forme n'habille plus la page : chaque projet a la sienne, dans son habit (chantier
 * habit-du-projet, ticket 03). Celle-ci, avec le type et le papier des fiches, est envoyée à la
 * création d'un projet, à l'ordinateur (`champsDUnProjetNeuf`, `creation.ts`) ; les listes de
 * `commun/`, jamais recopiées.
 *
 * Gardée sur l'appareil pour l'instant (décision 1 d'Igor du 2026-10-19). Plus tard elle passera
 * dans le compte, un réglage pour l'ordinateur, un pour le téléphone : tous les écrans la
 * demandent ici, à `useApparence`, et c'est seulement ce fichier qui changera.
 *
 * Le rangement de l'appareil peut manquer ou refuser (navigation privée, données bloquées) :
 * on le lit et on l'écrit dans un `try`, et l'app s'ouvre alors sur les défauts.
 */

export const AMBIANCES = ['carnet', 'clair', 'sombre'] as const;
export const FORMES = FORMES_DE_PROJET;
export const RELIEFS = ['doux', 'marque'] as const;

export type Ambiance = (typeof AMBIANCES)[number];
export type Forme = FormeDeProjet;
export type Relief = (typeof RELIEFS)[number];

/**
 * Après une bulle écrite dans la barre « + Bulle » (chantier modes-de-la-carte, modification 04,
 * décision d'Igor du 2026-10-21 : dans Paramètres, pas dans le projet) : écrire la suivante, comme
 * avant, ou ouvrir sa fiche.
 */
export const APRES_UNE_BULLE = ['suivante', 'fiche'] as const;
export type ApresUneBulle = (typeof APRES_UNE_BULLE)[number];

export interface Apparence {
  ambiance: Ambiance;
  /** Les titres écrits à la main (Caveat) ; non : en Figtree. */
  ecriture: boolean;
  relief: Relief;
  grain: boolean;
  forme: Forme;
  /** Le fond de la carte d'un projet neuf (chantier projet-neuf). */
  fondDeProjet: FondDeProjet;
  /** Le type des fiches d'un projet neuf. */
  typeDeFiche: TypeDeFiche;
  /** Le papier des fiches d'un projet neuf. */
  papierDeFiche: PapierDeFiche;
  /** L'angle sur lequel un projet neuf s'ouvre (chantier angle-a-l-ouverture). */
  angleALOuverture: AngleDOuverture;
  /** Après une bulle écrite dans la barre « + Bulle » : écrire la suivante, ou ouvrir sa fiche. */
  apresUneBulle: ApresUneBulle;
}

/**
 * Carnet papier et galet par défaut (décisions 2 et 5 d'Igor) ; écriture à la main, relief marqué
 * et grain, comme la maquette du carnet ; des fiches Classique, sur feuille blanche.
 */
export const APPARENCE_PAR_DEFAUT: Apparence = {
  ambiance: 'carnet',
  ecriture: true,
  relief: 'marque',
  grain: true,
  forme: 'galet',
  fondDeProjet: 'uni',
  typeDeFiche: FICHE_PAR_DEFAUT.type,
  papierDeFiche: FICHE_PAR_DEFAUT.papier,
  angleALOuverture: HABIT_PAR_DEFAUT.angle,
  apresUneBulle: 'suivante',
};

export const CLE_DE_L_APPARENCE = 'nextilt.apparence';

/** Ce qu'il faut du rangement de l'appareil (`localStorage`). */
export interface Rangement {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
}

/** Une valeur gardée qui n'est pas dans la liste retombe sur son défaut. */
function parmi<T extends string>(valeur: unknown, liste: readonly T[], defaut: T): T {
  return liste.includes(valeur as T) ? (valeur as T) : defaut;
}

/** Un oui ou non gardé qui n'est pas un booléen retombe sur son défaut. */
function ouiOuNon(valeur: unknown, defaut: boolean): boolean {
  return typeof valeur === 'boolean' ? valeur : defaut;
}

/** L'apparence gardée sur l'appareil, ou les défauts. Ne lève jamais. */
export function lireLApparence(rangement: () => Rangement | null | undefined): Apparence {
  try {
    const texte = rangement()?.getItem(CLE_DE_L_APPARENCE);
    if (!texte) return APPARENCE_PAR_DEFAUT;
    const lu: unknown = JSON.parse(texte);
    if (typeof lu !== 'object' || lu === null) return APPARENCE_PAR_DEFAUT;
    const {
      ambiance,
      ecriture,
      relief,
      grain,
      forme,
      fondDeProjet,
      typeDeFiche,
      papierDeFiche,
      angleALOuverture,
      apresUneBulle,
    } = lu as Record<string, unknown>;
    return {
      ambiance: parmi(ambiance, AMBIANCES, APPARENCE_PAR_DEFAUT.ambiance),
      ecriture: ouiOuNon(ecriture, APPARENCE_PAR_DEFAUT.ecriture),
      relief: parmi(relief, RELIEFS, APPARENCE_PAR_DEFAUT.relief),
      grain: ouiOuNon(grain, APPARENCE_PAR_DEFAUT.grain),
      forme: parmi(forme, FORMES, APPARENCE_PAR_DEFAUT.forme),
      fondDeProjet: parmi(fondDeProjet, FONDS_DE_PROJET, APPARENCE_PAR_DEFAUT.fondDeProjet),
      typeDeFiche: parmi(typeDeFiche, TYPES_DE_FICHE, APPARENCE_PAR_DEFAUT.typeDeFiche),
      papierDeFiche: parmi(papierDeFiche, PAPIERS_DE_FICHE, APPARENCE_PAR_DEFAUT.papierDeFiche),
      angleALOuverture: parmi(angleALOuverture, ANGLES_D_OUVERTURE, APPARENCE_PAR_DEFAUT.angleALOuverture),
      apresUneBulle: parmi(apresUneBulle, APRES_UNE_BULLE, APPARENCE_PAR_DEFAUT.apresUneBulle),
    };
  } catch {
    return APPARENCE_PAR_DEFAUT;
  }
}

/** Garde l'apparence sur l'appareil. Un rangement qui refuse ne fait rien d'autre que ne pas garder. */
export function ecrireLApparence(rangement: () => Rangement | null | undefined, apparence: Apparence): void {
  try {
    rangement()?.setItem(CLE_DE_L_APPARENCE, JSON.stringify(apparence));
  } catch {
    // Rien : l'app marche, elle se rouvrira simplement sur les défauts.
  }
}

/*
 * L'apparence de la page ouverte : lue une fois, puis tenue ici, pour que tout ce qui la montre
 * (la racine de la page, la page Paramètres) change dès qu'on choisit, sans recharger.
 */
const rangementDeLAppareil = () => window.localStorage;
const abonnes = new Set<() => void>();
let courante: Apparence | null = null;

function apparenceCourante(): Apparence {
  courante ??= lireLApparence(rangementDeLAppareil);
  return courante;
}

function sAbonner(abonne: () => void): () => void {
  abonnes.add(abonne);
  return () => abonnes.delete(abonne);
}

/** Change un réglage ou plusieurs, et le garde sur l'appareil. */
export function choisirLApparence(changement: Partial<Apparence>): void {
  courante = { ...apparenceCourante(), ...changement };
  ecrireLApparence(rangementDeLAppareil, courante);
  for (const abonne of abonnes) abonne();
}

/** L'apparence choisie : la seule question que les écrans posent. */
export function useApparence(): Apparence {
  return useSyncExternalStore(sAbonner, apparenceCourante, () => APPARENCE_PAR_DEFAUT);
}
